import {
  contactChannelLabels,
  formatPhone,
  type ContactNumber,
  type ContactChannel,
} from '@hue/contracts';
import { Button, IconButton, Input, PhoneInput, Switch, SelectAllToggle } from '@hue/design-system';
import { Plus, Trash2 } from 'lucide-react';

export function ContactNumbersEditor({
  value,
  onChange,
  disabled = false,
  error,
}: {
  value: ContactNumber[];
  onChange: (value: ContactNumber[]) => void;
  disabled?: boolean;
  error?: string | undefined;
}) {
  const update = (at: number, patch: Partial<ContactNumber>) =>
    onChange(value.map((item, index) => (index === at ? { ...item, ...patch } : item)));
  return (
    <fieldset disabled={disabled} className="grid min-w-0 gap-4">
      <legend className="mb-2 text-sm font-semibold">Telefones e aplicativos de mensagens</legend>
      <p className="text-sm text-content-muted">
        Cadastre cada número uma vez e marque os canais que ele atende. Somente contatos com Exibir
        na página pública serão divulgados.
      </p>
      {value.map((item, index) => (
        <div
          key={index}
          className="grid min-w-0 max-w-3xl gap-3 rounded-xl border border-surface-border p-3"
        >
          <div className="flex max-w-lg items-end gap-2">
            <div className="min-w-0 flex-1">
              <Input
                label={`Identificação do contato ${index + 1}`}
                value={item.label}
                maxLength={60}
                placeholder="Recepção, financeiro…"
                onChange={(event) => update(index, { label: event.target.value })}
              />
            </div>
            <IconButton
              label={`Remover contato ${index + 1}`}
              icon={<Trash2 />}
              tone="danger"
              onClick={() => onChange(value.filter((_, at) => at !== index))}
            />
          </div>
          <PhoneInput
            label={`Número do contato ${index + 1}`}
            value={formatPhone(item.number) ?? item.number}
            onValueChange={(number) => update(index, { number })}
          />
          <div className="max-w-sm">
            <SelectAllToggle
              total={3}
              selected={item.channels.length}
              scope={`Canais do contato ${index + 1}`}
              disabled={disabled}
              onSelectAll={() => update(index, { channels: ['CALL', 'WHATSAPP', 'TELEGRAM'] })}
              onClear={() => update(index, { channels: [] })}
            />
          </div>
          <div
            role="group"
            aria-label={`Canais do contato ${index + 1}`}
            className="flex flex-wrap gap-4"
          >
            {(Object.keys(contactChannelLabels) as ContactChannel[]).map((channel) => (
              <Switch
                key={channel}
                checked={item.channels.includes(channel)}
                onChange={(checked) =>
                  update(index, {
                    channels: checked
                      ? [...item.channels, channel]
                      : item.channels.filter((value) => value !== channel),
                  })
                }
              >
                {contactChannelLabels[channel]}
              </Switch>
            ))}
          </div>
          <Switch checked={item.isPublic} onChange={(isPublic) => update(index, { isPublic })}>
            Exibir contato {index + 1} na página pública
          </Switch>
        </div>
      ))}
      {error ? (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      ) : null}
      <div>
        <Button
          type="button"
          variant="outline"
          disabled={disabled || value.length >= 20}
          onClick={() =>
            onChange([...value, { label: '', number: '', channels: ['CALL'], isPublic: false }])
          }
        >
          <Plus className="size-4" aria-hidden="true" />
          Adicionar contato
        </Button>
      </div>
    </fieldset>
  );
}
